import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, inject, input, OnDestroy, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { BaasAppState, resolveValue } from '@lvflanders/bar';

import { _joinRouterCommands } from '../../../assets/join-router-commands.function';
import { BarActionButton } from '../../../bar-action-button/bar-action-button';
import { BarActionButtonOptions } from '../../../bar-action-button/bar-action-button-options.model';
import { _BarInternalLayoutService } from '../../../layout/bar-internal-layout.service';
import { BarMenuState } from '../../bar-menu-state.type';
import { BarLayoutService } from '../bar-layout.service';
import { _BarLayoutAnchorDirective } from '../bar-layout-anchor.directive';
import { BarLayoutMenuExternalLinksComponent } from './bar-layout-menu-external-links/bar-layout-menu-external-links.component';
import { BarLayoutMenuListComponent } from './bar-layout-menu-list/bar-layout-menu-list.component';
import { BarLayoutMenuOperationsComponent } from './bar-layout-menu-operations/bar-layout-menu-operations.component';
import { BarLayoutMenuStrikingActionsComponent } from './bar-layout-menu-striking-actions/bar-layout-menu-striking-actions.component';
import { BarMenuItemOptions } from './bar-menu-item-options.model';

@Component({
    selector: 'bar-layout-menu',
    templateUrl: './bar-layout-menu.component.html',
    host: { class: 'bar-layout-menu-root' },
    imports: [
        NgTemplateOutlet,
        _BarLayoutAnchorDirective,
        BarLayoutMenuStrikingActionsComponent,
        BarLayoutMenuListComponent,
        BarLayoutMenuExternalLinksComponent,
        BarLayoutMenuOperationsComponent,
        BarActionButton,
        NgClass
    ]
})
export class BarLayoutMenuComponent implements OnDestroy {
    readonly internalLayout = inject(_BarInternalLayoutService);
    readonly #destroyRef = inject(DestroyRef);
    readonly #router = inject(Router);
    readonly #layout = inject(BarLayoutService);
    readonly #state = inject(BaasAppState, { optional: true });

    readonly mobileFriendly = input<boolean>(undefined);
    readonly barLayoutMenuAnchors = viewChildren(_BarLayoutAnchorDirective);
    items: BarMenuItemOptions[] = [];
    #routeSubscriber;
    #stateMapping;

    get mainAction(): BarActionButtonOptions {
        return this.#layout.mainAction$.getValue() || this.internalLayout.internalMainAction;
    }

    get menuAction(): BarActionButtonOptions {
        return this.#layout.menuAction;
    }

    get smallDevice() {
        return this.internalLayout.smallDevice;
    }

    get menuState() {
        return this.#layout.menuState$.getValue();
    }

    get menuToggleMetaIcon() {
        return this.#stateMapping[this.menuState ? this.menuState : 'default'].icon;
    }

    get menuToggleMetaText() {
        return this.#stateMapping[this.menuState ? this.menuState : 'default'].text;
    }

    constructor() {
        this.init();
    }

    ngOnDestroy() {
        if (this.#routeSubscriber) this.#routeSubscriber.unsubscribe();
    }

    menuToggleClicked(event) {
        this.#stateMapping[this.menuState ? this.menuState : 'default'].action();
    }

    setMenuState(menuState: BarMenuState): void {
        this.#layout.menuState$.next(menuState);
    }

    private init() {
        /* Default info: window width < 1200px equals overlay mode, > 1200px equals pinned mode */
        this.#stateMapping = {
            closed: {
                icon: 'fas fa-caret-right',
                text: 'Menu openen',
                action: () => {
                    this.#layout.menuState$.next('overlay');
                }
            },
            pinned: {
                icon: 'fas fa-caret-left',
                text: 'Menu sluiten',
                action: () => {
                    this.#layout.menuState$.next(this.mobileFriendly() ? 'overlay' : 'closed');
                }
            },
            overlay: {
                icon: 'fas fa-caret-right',
                text: 'Menu pinnen',
                action: () => {
                    this.#layout.menuState$.next(this.mobileFriendly() ? 'pinned' : 'pinned');
                }
            },
            default: {
                icon: this.smallDevice ? 'fas fa-caret-right' : 'fas fa-caret-left',
                text: this.smallDevice ? 'Menu pinnen' : 'Menu sluiten',
                action: () => {
                    this.smallDevice ? this.#layout.menuState$.next('pinned') : this.#layout.menuState$.next(this.mobileFriendly() ? 'overlay' : 'closed');
                }
            }
        };

        this.#layout.menuItems$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((items: BarMenuItemOptions[]) => {
            if (items && items.length) {
                this.items = this.#setExpandedStateForActiveItems(items);
                this.#findMenuItemRecursive(items, '');
            } else {
                this.items = [];
            }
        });

        this.#layout.menuItemsAutoCollapse$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((autoCollapse: boolean) => {
            if (autoCollapse) {
                this.#routeSubscriber = this.#router.events.subscribe((event) => {
                    if (event instanceof NavigationEnd) {
                        // close inactive submenu's
                        const collapsedMenus = this.#closeInactiveSubmenus(this.#layout.menuItems$.getValue());
                        // set new menu (only active tree is expanded)
                        this.#layout.menuItems$.next(collapsedMenus);
                    }
                });
            } else if (this.#routeSubscriber) {
                this.#routeSubscriber.unsubscribe();
            }
        });
    }

    /** Sets the items expand property to true if a subItem is active, if not to false */
    #setExpandedStateForActiveItems(items: BarMenuItemOptions[]): BarMenuItemOptions[] {
        return items.map((item: BarMenuItemOptions) => {
            // only set expanded items for the current application
            if (!item.appCode || item.appCode === this.#state.toepassing.code) {
                // first check if expanded has been preconfigured, if so skip checking
                if (!item.expanded) {
                    if (item.routerLink || item.routerUrl) {
                        const routerUrl: string = item.routerLink ? _joinRouterCommands(resolveValue(item.routerLink)) : resolveValue(item.routerUrl);
                        let hasActiveSubChild: boolean;

                        if (item.children && item.children.length) hasActiveSubChild = this.#hasSubItemWithActiveRouterPath(item.children);

                        item.expanded = hasActiveSubChild || window.location.pathname.includes(routerUrl);
                    } else if (item.children && item.children.length) {
                        item.expanded = this.#hasSubItemWithActiveRouterPath(item.children);
                    }
                }

                // if expanded, continue checking the node's tree to expand further
                if (item.expanded && item.children && item.children.length) this.#setExpandedStateForActiveItems(item.children);
            }

            return item;
        });
    }

    /** Check if an item with children has an active routerLink
     * return true if a subChild is active (can be nested inifitely deep)
     * return false if no subChild is active
     **/
    #hasSubItemWithActiveRouterPath(items: BarMenuItemOptions[]) {
        const itemHasActiveSubItem: any = items.find((subItem) => {
            if (subItem.routerLink || subItem.routerUrl) {
                const routerUrl: string = subItem.routerLink ? _joinRouterCommands(resolveValue(subItem.routerLink)) : resolveValue(subItem.routerUrl);
                // when routerlink is in pathname always return true
                const match = window.location.pathname.includes(routerUrl);
                if (match) return true;
            }

            // no routerLink
            // or routerLink is not in the pathname
            // then continue checking the node's tree
            if (subItem.children && subItem.children.length) return this.#hasSubItemWithActiveRouterPath(subItem.children);

            return false;
        });

        return !!itemHasActiveSubItem;
    }

    /**
     * Auto mechanism to collapse inactiv expanded submenus
     * We set all the expanded properties to false
     * Due to the subscription of menuItems$ the setExpandedStateForActiveItems method will be called and will set only the active sub menu to an active state
     */
    #closeInactiveSubmenus(menuItems) {
        return menuItems.map((item: BarMenuItemOptions): BarMenuItemOptions => {
            item.expanded = false;
            if (item.children && item.children.length) item.children = this.#closeInactiveSubmenus(item.children);
            return item;
        });
    }

    #getPath(path, index) {
        return path.length ? `${path}.${index}` : `${index}`;
    }

    #findMenuItemRecursive(menuItems: BarMenuItemOptions[], path): BarMenuItemOptions {
        for (let index = 0; index < menuItems.length; index++) {
            const menuItem = menuItems[index];

            if (menuItem.routerLink) {
                const urlTree = this.#router.createUrlTree(resolveValue(menuItem.routerLink));
                if (this.#router.isActive(urlTree, true)) {
                    // active item found, set it expanded (does not mather if doens't have child items)
                    menuItem.expanded = true;
                    return menuItem;
                }
            }

            if (menuItem.children && menuItem.children.length) {
                const subMenuItem = this.#findMenuItemRecursive(menuItem.children, this.#getPath(path, index));
                if (subMenuItem) return subMenuItem;
            }
        }

        return null;
    }
}
